// CampusShell · shell PERSISTENTE del campus v2 (anti-parpadeo)
// ─────────────────────────────────────────────────────────────────────────
// PROBLEMA que resuelve: antes cada página renderizaba su propio
// `<Layout><AppShell>…`. Como cada ruta es un componente de página distinto,
// al navegar React DESMONTABA toda la subárbol (sidebar + barra superior +
// contenido) y MONTABA otra desde cero. Ese tear-down/rebuild del chrome en
// cada navegación era el PARPADEO que se veía (rail + topbar + logo recargando,
// lectura de localStorage del estado plegado, etc.).
//
// SOLUCIÓN: este componente es un *layout route* de react-router. Monta el
// AppShell (sidebar + barra superior) UNA sola vez y renderiza el contenido de
// la ruta activa en su `<Outlet/>`. Así, al navegar, SOLO cambia el contenido
// del Outlet; el rail y la topbar PERSISTEN (no se desmontan) → sin parpadeo.
//
// Las páginas siguen envolviendo su contenido en `<Layout>` sin cambios: cuando
// Layout detecta (vía InsideShellContext) que ya vive dentro de este shell
// persistente, se comporta en modo "solo contenido" (renderiza children + pie
// legal, sin volver a montar el AppShell ni los banners). Ver Layout.tsx.

import { createContext, Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { isStubModeAllowed } from '../lib/env';
import { fetchProfile, type UserProfile } from '../lib/api';
import AppShell from './AppShell';
import CookieBanner from './CookieBanner';
import ImpersonationBanner from './ImpersonationBanner';
import TutorSidebar from './TutorSidebar';
import { ExternalLinksProvider } from '../lib/external-links';
import { DemoExpiredBanner } from './DemoCampusOverlay';
import DemoQuestionnaire from './DemoQuestionnaire';

/** True cuando el árbol ya está dentro del shell persistente. Layout lo lee
 *  para no volver a montar el AppShell (evita doble chrome y el remount). */
const InsideShellContext = createContext(false);

/** ¿Estamos dentro del CampusShell persistente? Lo usa Layout. */
export function useInsideShell(): boolean {
  return useContext(InsideShellContext);
}

/** Expone si el usuario activo es un usuario demo-start, y si ha expirado.
 *  Basado en el profile ya cargado por CampusShell (sin llamadas extra). */
interface DemoPlanCtx {
  isDemo: boolean;
  demoLockedAt: string | null;
}
const DemoPlanContext = createContext<DemoPlanCtx>({ isDemo: false, demoLockedAt: null });
export function useDemoPlan(): DemoPlanCtx { return useContext(DemoPlanContext); }

// ───────────────────────────────────────────────────────────────────────────
// Tutor IA GLOBAL (Jaime · 2026-06-17): el Tutor IA debe estar en TODAS las
// páginas (antes solo en Dashboard y ModuleDetail). Lo montamos UNA vez aquí
// (persistente, como el resto del shell). Las páginas con contexto rico
// (curso/módulo) lo registran vía `useSetTutorContext` para que el tutor
// responda según ese contexto; el resto de páginas quedan en scope general
// (comercial/soporte). Sin contexto → courseSlug/moduleSlug null.
// ───────────────────────────────────────────────────────────────────────────
interface TutorRouteCtx {
  moduleSlug: string | null;
  courseSlug: string | null;
  moduleTitle: string | null;
}
const DEFAULT_TUTOR_CTX: TutorRouteCtx = {
  moduleSlug: null,
  courseSlug: null,
  moduleTitle: null,
};
const TutorSetterContext = createContext<(ctx: TutorRouteCtx) => void>(() => {});

/**
 * Registra el contexto del Tutor IA global para la ruta actual. Lo llaman las
 * páginas con curso/módulo (Curso, ModuleDetail). Se resetea al desmontar, así
 * que las demás páginas vuelven al scope general automáticamente.
 */
export function useSetTutorContext(ctx: Partial<TutorRouteCtx>): void {
  const setCtx = useContext(TutorSetterContext);
  const moduleSlug = ctx.moduleSlug ?? null;
  const courseSlug = ctx.courseSlug ?? null;
  const moduleTitle = ctx.moduleTitle ?? null;
  useEffect(() => {
    setCtx({ moduleSlug, courseSlug, moduleTitle });
    return () => setCtx(DEFAULT_TUTOR_CTX);
  }, [setCtx, moduleSlug, courseSlug, moduleTitle]);
}

// ── Demo route guard (F-DEMO-START-001) ─────────────────────────────────────
// Rutas exactas permitidas para usuarios demo-start.
const DEMO_ALLOWED_EXACT = new Set([
  '/dashboard',
  '/curso/robotea-start',
  '/curso/robotea-start/bonos',
  '/my-robots',
  '/perfil',
  '/notificaciones',
  '/guia',
  '/buzon',
  '/tienda-eas',
  '/comunidad',
  '/progreso',
]);
// Prefijos de ruta permitidos para demo (cualquier subruta bajo estos paths).
const DEMO_ALLOWED_PREFIXES = [
  '/dashboard/modulo/', // lecciones F1+F2 (backend rechaza F3/F4)
];

function isDemoAllowed(pathname: string): boolean {
  if (DEMO_ALLOWED_EXACT.has(pathname)) return true;
  return DEMO_ALLOWED_PREFIXES.some((p) => pathname.startsWith(p));
}

const STAFF_ROLES = new Set(['owner', 'admin', 'soporte', 'tutor', 'staff']);

/**
 * Redirige a /dashboard si el usuario demo intenta acceder a una ruta no habilitada.
 * Staff y owners quedan exentos aunque tengan demo_plan por error de configuración.
 * Se monta dentro del DemoPlanContext (que solo existe dentro de CampusShell).
 */
function DemoRouteGuard(): JSX.Element | null {
  const { isDemo, demoLockedAt } = useDemoPlan();
  const { state } = useAuth();
  const { pathname } = useLocation();
  // Demo expirado → DemoExpiredBanner cubre todo; no redirigir adicionalmente.
  if (!isDemo || demoLockedAt) return null;
  // Staff/owners no quedan bloqueados aunque tengan demo_plan asignado por error.
  const role = state.status === 'authenticated' ? state.identity.role : null;
  if (role && STAFF_ROLES.has(role)) return null;
  if (!isDemoAllowed(pathname)) return <Navigate to="/dashboard" replace />;
  return null;
}

/**
 * Layout route que monta el AppShell persistente una sola vez y renderiza el
 * contenido de la ruta en su Outlet. Envuelve a todas las rutas autenticadas.
 *
 * Sólo monta el AppShell cuando hay sesión autenticada (igual que el contrato
 * previo de Layout). Mientras la sesión carga o si no hay sesión, delega en el
 * Outlet a pelo: la propia página (vía su RequireAuth/Layout) gestiona el
 * estado de carga o el redirect a /login sin chrome, como antes.
 */
export default function CampusShell(): JSX.Element {
  const { state } = useAuth();
  const authenticated = state.status === 'authenticated';
  const identity = state.status === 'authenticated' ? state.identity : null;
  // Contexto del Tutor IA global (lo registran las páginas con curso/módulo).
  // Debe declararse ANTES del early-return para no romper las reglas de hooks.
  const [tutorCtx, setTutorCtx] = useState<TutorRouteCtx>(DEFAULT_TUTOR_CTX);

  // ── Perfil demo (F-DEMO-START-001) ─────────────────────────────────────────
  // Cargamos el perfil del usuario una vez por sesión para leer los campos demo.
  // Mismo patrón que OnboardingWizard: fetchProfile best-effort, sin bloquear.
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const lastEmailRef = useRef<string | null>(null);

  const loadProfile = useCallback(
    async (auth: typeof identity): Promise<void> => {
      if (!auth) return;
      try {
        const data = await fetchProfile(auth);
        setProfile(data);
      } catch {
        // Best-effort · si falla no bloquea el campus
      }
    },
    [],
  );

  useEffect(() => {
    if (!identity) return;
    // Reset si cambia de usuario (signOut + signIn diferente)
    if (lastEmailRef.current && lastEmailRef.current !== identity.email) {
      setProfile(null);
    }
    lastEmailRef.current = identity.email;
    void loadProfile(identity);
  }, [identity, loadProfile]);

  // Refresca el perfil cuando DemoQuestionnaire completa el onboarding.
  useEffect(() => {
    const handler = (): void => { void loadProfile(identity); };
    window.addEventListener('profile:refresh', handler);
    return () => window.removeEventListener('profile:refresh', handler);
  }, [identity, loadProfile]);

  // Sin sesión confirmada todavía (loading) o no autenticado: no montamos el
  // shell. El Outlet renderiza la página, que ya trae su propio gate. Esto
  // preserva el comportamiento previo (spinner de RequireAuth / Navigate).
  if (!authenticated) {
    return <Outlet />;
  }

  const showStubBanner = isStubModeAllowed() && state.identity.is_stub;
  const showReviewBanner = state.identity.mode === 'review';

  return (
    <DemoPlanContext.Provider value={{ isDemo: profile?.demo_plan === 'demo-start', demoLockedAt: profile?.demo_locked_at ?? null }}>
    <ExternalLinksProvider auth={state.identity}>
    <TutorSetterContext.Provider value={setTutorCtx}>
    <InsideShellContext.Provider value={true}>
      {/* Demo guards (F-DEMO-START-001) — rendered inside shell so they have auth context */}
      {profile?.demo_plan === 'demo-start' && (
        <>
          <DemoExpiredBanner demoLockedAt={profile.demo_locked_at ?? null} />
          {!profile.demo_locked_at && !profile.demo_onboarding_completed_at && (
            <DemoQuestionnaire whatsapp={profile.whatsapp ?? null} identity={identity} />
          )}
        </>
      )}
      {/* Redirige rutas no habilitadas para demo — usa DemoPlanContext ya montado */}
      <DemoRouteGuard />
      {/* Columna a altura EXACTA del viewport (h-screen). Los banners (stub /
          preview) van arriba con altura propia; el AppShell ocupa el resto con
          `flex-1 min-h-0`. Así el rail mide la altura REALMENTE disponible (no
          un 100vh fijo que ignora el banner y empujaba el pie del sidebar fuera
          de pantalla). Sin banner, ocupa el viewport completo igual. */}
      <div className="flex h-screen flex-col overflow-hidden">
        {/* Banner de impersonación · arriba del todo, fijo, con botón Salir. */}
        <ImpersonationBanner />
        {showStubBanner && (
          <div
            role="alert"
            className="shrink-0 border-b border-amber-400/40 bg-amber-500/15 px-4 py-2 text-center text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-200"
          >
            MODO STUB · auth fake (X-User-Email) · NO USAR EN PROD
          </div>
        )}
        {showReviewBanner && (
          <div
            role="alert"
            data-testid="review-mode-banner"
            className="shrink-0 border-b border-brand-violet/40 bg-brand-violet/15 px-4 py-2 text-center text-xs font-semibold uppercase tracking-wider text-brand-indigo dark:text-brand-violet"
          >
            MODO PREVIEW · sesión de revisión · datos limitados
          </div>
        )}
        <div className="min-h-0 flex-1">
          <AppShell>
            {/* Boundary de Suspense DENTRO del shell · CLAVE anti-parpadeo.
                Las páginas se cargan con React.lazy (chunks aparte). Si el boundary
                estuviera ARRIBA del shell (como estaba en App.tsx), al cargar el
                chunk de una sección React DESMONTABA todo el shell y pintaba el
                spinner a pantalla completa → flash en blanco en CADA clic del menú.
                Aquí el boundary envuelve SOLO el contenido: el rail y la barra
                superior nunca se desmontan. `fallback={null}` + la navegación con
                startTransition (ver AppShell navTransition) mantienen el contenido
                ANTERIOR visible hasta que el nuevo está listo → sin spinner ni
                hueco en blanco. */}
            <Suspense fallback={null}>
              <Outlet />
            </Suspense>
          </AppShell>
        </div>
      </div>
      {/* Tutor IA GLOBAL · montado una vez, en TODAS las páginas. El contexto
          (curso/módulo) lo registran las páginas vía useSetTutorContext; sin
          contexto queda en scope general (comercial/soporte). */}
      <TutorSidebar
        moduleSlug={tutorCtx.moduleSlug}
        courseSlug={tutorCtx.courseSlug}
        moduleTitle={tutorCtx.moduleTitle}
        auth={state.identity}
      />
      <CookieBanner />
    </InsideShellContext.Provider>
    </TutorSetterContext.Provider>
    </ExternalLinksProvider>
    </DemoPlanContext.Provider>
  );
}
